import React from 'react';
import { Box } from '@mui/material';
import { GLASS_BG, GLASS_BORDER, GLASS_BORDER_HOVER, PRIMARY, SUCCESS, ERROR } from '../colors';

/**
 * GlassCard - Modern glassmorphism card component
 * Inspired by umbrelOS design language
 * 
 * @param {string} variant - 'default' | 'elevated' | 'outlined' | 'gradient'
 * @param {boolean} hover - Enable hover lift effect
 * @param {boolean} glow - Enable glow effect
 * @param {string} glowColor - Custom glow color
 * @param {number} blur - Backdrop blur amount
 * @param {object} sx - Additional MUI sx styles
 */
const GlassCard = ({
  children,
  variant = 'default',
  hover = true,
  glow = false,
  glowColor = 'rgba(59, 130, 246, 0.24)',
  blur = 14,
  sx = {},
  ...props
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'elevated':
        return {
          background: 'rgba(17, 29, 45, 0.92)',
          border: `1px solid ${GLASS_BORDER_HOVER}`,
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.24)',
        };
      case 'outlined':
        return {
          background: 'transparent',
          border: `1px solid ${GLASS_BORDER_HOVER}`,
          boxShadow: 'none',
        };
      case 'gradient':
        return {
          background: 'rgba(59, 130, 246, 0.10)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          boxShadow: '0 8px 28px rgba(59, 130, 246, 0.1)',
        };
      case 'dark':
        return {
          background: 'rgba(11, 19, 32, 0.88)',
          border: `1px solid ${GLASS_BORDER}`,
          boxShadow: '0 8px 28px rgba(0, 0, 0, 0.3)',
        };
      default:
        return {
          background: GLASS_BG,
          border: `1px solid ${GLASS_BORDER}`,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
        };
    }
  };

  const hoverStyles = hover ? {
    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: glow 
        ? `0 20px 60px ${glowColor}`
        : '0 14px 36px rgba(0, 0, 0, 0.28)',
      borderColor: `${PRIMARY}80`,
    },
  } : {};

  const glowStyles = glow ? {
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: -1,
      background: `linear-gradient(135deg, ${glowColor} 0%, transparent 50%)`,
      borderRadius: 'inherit',
      zIndex: -1,
      opacity: 0.5,
      filter: 'blur(20px)',
    },
  } : {};

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '16px',
        backdropFilter: `blur(${blur}px)`,
        WebkitBackdropFilter: `blur(${blur}px)`,
        overflow: 'hidden',
        ...getVariantStyles(),
        ...hoverStyles,
        ...glowStyles,
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

/**
 * GlassCardContent - Styled content wrapper for GlassCard
 */
export const GlassCardContent = ({ children, sx = {}, ...props }) => (
  <Box
    sx={{
      padding: { xs: 2, sm: 3 },
      ...sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

/**
 * FeatureCard - Card specifically designed for feature highlights
 */
export const FeatureCard = ({
  icon,
  title,
  description,
  accentColor = 'rgba(59, 130, 246, 0.3)',
  iconBgColor,  // alias for accentColor - extract to prevent DOM pollution
  sx = {},
  ...props
}) => {
  // Use iconBgColor if provided (legacy prop), otherwise accentColor
  const bgColor = iconBgColor || accentColor;
  
  return (
    <GlassCard 
      hover 
      glow
      glowColor={bgColor}
      sx={{ height: '100%', ...sx }}
      {...props}
    >
    <GlassCardContent>
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: '16px',
          background: `linear-gradient(135deg, ${bgColor} 0%, transparent 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          '& svg': {
            fontSize: 28,
            color: 'white',
          },
        }}
      >
        {icon}
      </Box>
      <Box
        component="h3"
        sx={{
          fontSize: '1.25rem',
          fontWeight: 700,
          mb: 1.5,
          color: 'white',
          margin: 0,
        }}
      >
        {title}
      </Box>
      <Box
        component="p"
        sx={{
          fontSize: '0.95rem',
          color: 'rgba(255, 255, 255, 0.7)',
          lineHeight: 1.7,
          margin: 0,
        }}
      >
        {description}
      </Box>
    </GlassCardContent>
  </GlassCard>
  );
};

/**
 * StatsCard - Card for displaying statistics/metrics
 */
export const StatsCard = ({
  value,
  label,
  icon,
  trend,
  trendUp = true,
  accentColor = 'rgba(59, 130, 246, 0.3)',
  sx = {},
  ...props
}) => (
  <GlassCard variant="gradient" sx={{ ...sx }} {...props}>
    <GlassCardContent sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        {icon && (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              background: accentColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& svg': {
                fontSize: 24,
                color: 'white',
              },
            }}
          >
            {icon}
          </Box>
        )}
        {trend && (
          <Box
            sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: trendUp ? SUCCESS : ERROR,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {trendUp ? '↑' : '↓'} {trend}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          fontSize: 'clamp(2rem, 4vw, 2.5rem)',
          fontWeight: 800,
          background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.8) 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          mb: 0.5,
        }}
      >
        {value}
      </Box>
      <Box
        sx={{
          fontSize: '0.875rem',
          color: 'rgba(255, 255, 255, 0.6)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontWeight: 500,
        }}
      >
        {label}
      </Box>
    </GlassCardContent>
  </GlassCard>
);

/**
 * LiveTile - Status indicator tile (inspired by umbrelOS LiveTile)
 */
export const LiveTile = ({
  title,
  status,
  statusColor = SUCCESS,
  icon,
  value,
  subtitle,
  sx = {},
  ...props
}) => (
  <GlassCard variant="dark" hover sx={{ ...sx }} {...props}>
    <GlassCardContent sx={{ py: 2, px: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& svg': {
                fontSize: 20,
                color: 'white',
              },
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box
              component="span"
              sx={{
                fontSize: '0.9rem',
                fontWeight: 600,
                color: 'white',
              }}
            >
              {title}
            </Box>
            {status && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: statusColor,
                  boxShadow: `0 0 10px ${statusColor}`,
                }}
              />
            )}
          </Box>
          {value && (
            <Box sx={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }}>
              {value}
            </Box>
          )}
        </Box>
      </Box>
    </GlassCardContent>
  </GlassCard>
);

export default GlassCard;
